import type { ReactNode } from 'react'
import { useState } from 'react'
import { Popover } from './Popover.js'

/**
 * The `⋯` that opens a menu of things to do to one thing.
 *
 * Four of these exist — a contacts column heading, a segments column heading, a
 * segment row, a view tab — and every one of them was the same three parts: a
 * trigger that appears on hover and on focus, a panel, and the rule that
 * choosing an item closes the menu before acting. `jscpd` found the third copy.
 *
 * **It appears on hover and whenever it has focus.** A control that only shows
 * under a pointer is one nobody tabbing can find.
 *
 * **Choosing closes first, then acts.** An item that opened a dialog while the
 * menu was still up left the menu floating over it, and an item that acted
 * first could unmount its own menu mid-click.
 */
export function EllipsisMenu({
  label,
  hover,
  width = 'w-52',
  children,
}: {
  /** What the trigger is called to a screen reader. Names the thing, not the dots. */
  readonly label: string
  /**
   * The hover group whose name reveals this.
   *
   * Tailwind needs the literal class, so these are spelt out rather than built:
   * a class assembled at runtime is one that is not in the stylesheet.
   */
  readonly hover: 'head' | 'row' | 'tab'
  readonly width?: 'w-52' | 'w-56'
  /** The items. `choose` closes the menu and then runs what was picked. */
  readonly children: (choose: (act: () => void) => () => void) => ReactNode
}) {
  const [open, setOpen] = useState(false)

  const choose = (act: () => void) => () => {
    setOpen(false)
    act()
  }

  return (
    <Popover
      align="right"
      label={label}
      onClose={() => setOpen(false)}
      onToggle={() => setOpen((was) => !was)}
      open={open}
      panelClassName={`${width} p-1`}
      trigger="⋯"
      triggerClassName={`rounded-field px-1.5 text-[14px] text-t3 leading-none hover:bg-line-2 hover:text-t1 ${
        open ? '' : `opacity-0 focus-visible:opacity-100 ${REVEALED[hover]}`
      }`}
    >
      {children(choose)}
    </Popover>
  )
}

/** Literal classes, because Tailwind builds its stylesheet by reading source. */
const REVEALED = {
  head: 'group-hover/head:opacity-100',
  row: 'group-hover:opacity-100',
  tab: 'group-hover/tab:opacity-100',
} as const
